<template>
    <div class="add customer__form modal modal--default">
        <div class="modal__panel" style="min-height: 500px;">
            <div class="modal__logo-wrapper">
                <div class="modal__logo"></div>
            </div>
            <div class="add__new">{{ $t(`settingsPage.${"provisionImport"}`) }}</div>
            <div class="modal__main-icon modal__main-icon--provision"></div>
            <div class="modal__close" @click="closeModal()"></div>

            <div class="form">
                <div v-if="isRequesting" class="requesting-el"></div>
                <div v-else class="add__form import__form"> 
                    <div class="form__row team-row" v-if="teams && teams.length > 0">
                        <MultiSelectDropdown
                            :items="teams"
                            :placeholder="$t('settingsPage.selectTeamPlaceholder')"
                            label="name"
                            :reduce="(team) => team.id"
                            :selected="teamIds"
                            @input="teamIds = $event"
                        />
                    </div>
                    <div class="form__row" v-if="teamIds.length === 0">
                        <p style="color: var(--text-2); text-align: center;">{{ $t(`settingsPage.${"selectTeam"}`) }}.</p>
                    </div>

                    <div class="form__row" v-if="teamIds.length > 0">
                        <DropFile ref="fileInput" v-if="showInputFile" @input="handleFileUpload" />
                    </div>
                    <div class="form__row" v-if="teamIds.length > 0">
                        <div class="sample__csv">
                            <a v-if="showInputFile" href="/sample_import.xlsx" class="btn">{{ $t(`settingsPage.${"downloadSample"}`) }}</a>
                        </div>
                    </div>

                    <div class="map__fields" v-if="headers.length">
                        <div class="form__row">
                            <h4 class="form__title">{{ $t(`settingsPage.${"cvsFields"}`) }}:</h4>
                        </div>
                        <div class="form__row">
                            <div class="form__field form__field--double" style="margin-right: 10px;">
                                <VSelect
                                    :options="headers"
                                    :reduce="(header) => header"
                                    :placeholder="$t('settingsPage.name')"
                                    label="name"
                                    v-model="nameField"
                                    class="select select--default"
                                />
                            </div>
                            <div class="form__field form__field--double" style="margin-right: 10px;">
                                <VSelect
                                    :options="headers"
                                    :reduce="(header) => header"
                                    :placeholder="$t('settingsPage.policyNr')"
                                    label="policy"
                                    v-model="policyNumberField"
                                    class="select select--default"
                                />
                            </div>
                            <div class="form__field form__field--double" style="margin-right: 10px;">
                                <VSelect
                                    :options="headers"
                                    :reduce="(header) => header"
                                    :placeholder="$t('settingsPage.provision')"
                                    label="provision"
                                    v-model="provisionField"
                                    class="select select--default"
                                />
                            </div>
                        </div>
                        <div class="form__row">
                            <div class="form__field form__field--double" style="margin-right: 10px;">
                                <VSelect
                                    :options="headers"
                                    :reduce="(header) => header"
                                    :placeholder="$t('settingsPage.cancellationDate')"
                                    label="date"
                                    v-model="dateField"
                                    class="select select--default"
                                />
                            </div>
                            <div class="form__field form__field--double" style="margin-right: 10px;">
                                <VSelect
                                    :options="headers"
                                    :reduce="(header) => header"
                                    :placeholder="$t('settingsPage.company')"
                                    label="company"
                                    v-model="companyField"
                                    class="select select--default"
                                />
                            </div>
                            <div class="form__field form__field--double">
                                <VSelect
                                    :options="headers"
                                    :reduce="(header) => header"
                                    :placeholder="$t('settingsPage.branch')"
                                    label="branch"
                                    v-model="branchField"
                                    class="select select--default"
                                />
                            </div>
                        </div>

                        <button
                            v-if="headers.length && !showCvsData && !resultPreview && $checkRolePermission('provision_import_preview')"
                            :class="{ 'disabled': !canProcessCSV }"
                            class="modal__button modal__button--submit button button--default"
                            @click="processMappedData"
                        >
                            <span>{{ $t(`settingsPage.${"processCsv"}`) }}</span>
                        </button>
                    </div>

                    <div v-if="showCvsData && csvData.length && teamIds.length > 0" class="map__fields processed__table">
                        <h4 class="form__title">{{ $t(`settingsPage.${"parsedCsv"}`) }}:</h4>
                        <table>
                            <thead>
                                <tr>
                                    <th>{{ $t('settingsPage.name') }}</th>
                                    <th>{{ $t('settingsPage.policyNr') }}</th>
                                    <th>{{ $t('settingsPage.provision') }}</th>
                                    <th>{{ $t('settingsPage.date') }}</th>
                                    <th>{{ $t('settingsPage.company') }}</th>
                                    <th>{{ $t('settingsPage.branch') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in csvData" :key="index">
                                    <td>{{ item.name }}</td>
                                    <td>{{ item.policyNumber }}</td>
                                    <td>{{ item.provision }}</td>
                                    <td>{{ item.date }}</td>
                                    <td>{{ item.company }}</td>
                                    <td>{{ item.branch }}</td>
                                </tr>
                            </tbody>
                        </table>

                        <button
                            v-if="showCvsData && $checkRolePermission('provision_import_preview')"
                            class="modal__button modal__button--submit button button--default"
                            @click="sendDataToPreview"
                        >
                            <span>{{ $t(`buttons.${"preview"}`) }}</span>
                        </button>
                    </div>

                    <div v-if="skippedRows.length > 0" class="map__fields skipped__table">
                        <h4 class="form__title">{{ $t('settingsPage.skippedRows') }} ({{ skippedRows.length }}):</h4>
                        <table>
                            <thead>
                                <tr>
                                    <th>{{ $t('settingsPage.row') }} #</th>
                                    <th>{{ $t('settingsPage.reason') }}</th>
                                    <th>{{ $t('settingsPage.rawData') }}</th>
                                    <th>{{ $t('settingsPage.fieldsFound') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in skippedRows" :key="index">
                                    <td>{{ item.rowNumber }}</td>
                                    <td class="error-message">{{ item.reason }}</td>
                                    <td class="raw-data">{{ item.rawLine }}</td>
                                    <td>{{ item.fields ? item.fields.length : 0 }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="resultPreview && (resultPreview.successfullyImported.length || resultPreview.failedImports.length)" class="results">
                        <h4>{{ $t('settingsPage.importResults') }}</h4>
                        <div v-if="resultPreview.successfullyImported.length">
                            <h5>{{ $t('settingsPage.successImports') }}:</h5>
                            <div class="wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{{ $t('settingsPage.name') }}</th>
                                            <th>{{ $t('settingsPage.policyNr') }}</th>
                                            <th>{{ $t('settingsPage.team') }}</th>
                                            <th>{{ $t('settingsPage.agent') }}</th>
                                            <th>{{ $t('settingsPage.provision') }}</th>
                                            <th>{{ $t('settingsPage.date') }}</th>
                                            <th>{{ $t('settingsPage.company') }}</th>
                                            <th>{{ $t('settingsPage.branch') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, index) in resultPreview.successfullyImported" :key="index">
                                            <td>{{ item.fullname }}</td>
                                            <td>{{ item.policyNumber }}</td>
                                            <td>{{ item.team }}</td>
                                            <td>{{ item.agent }}</td>
                                            <td>{{ item.provision }}</td>
                                            <td>{{ item.date }}</td>
                                            <td>{{ item.company }}</td>
                                            <td>{{ item.branch }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-if="resultPreview.failedImports.length">
                            <h5>{{ $t('settingsPage.failedImports') }}:</h5>
                            <div class="wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{{ $t('settingsPage.name') }}</th>
                                            <th>{{ $t('settingsPage.policyNr') }}</th>
                                            <th>{{ $t('settingsPage.provision') }}</th>
                                            <th>Error</th>
                                            <th>{{ $t('settingsPage.date') }}</th>
                                            <th>{{ $t('settingsPage.company') }}</th>
                                            <th>{{ $t('settingsPage.branch') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, index) in resultPreview.failedImports" :key="index">
                                            <td>{{ item.fullname }}</td>
                                            <td>{{ item.policyNumber }}</td>
                                            <td>{{ item.provision }}</td>
                                            <td class="error-message">{{ item.error }}</td>
                                            <td>{{ item.date }}</td>
                                            <td>{{ item.company }}</td>
                                            <td>{{ item.branch }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <button
                            class="modal__button modal__button--submit button button--default"
                            style="left: 30px;"
                            @click="closeModal()"
                        >
                            {{ $t(`buttons.${"cancelBtn"}`) }}
                        </button>

                        <div class="download-buttons" v-if="$checkRolePermission('provision_import_preview')">
                            <button
                                v-if="resultPreview.successfullyImported.length"
                                class="modal__button modal__button--submit button button--default button--download download-error-btn"
                                @click="downloadSuccessRowsDirectly"
                            >
                                <img src="../assets/icons/default/download_icon.svg" /> <br />
                                <p>{{ $t(`settingsPage.${"downloadSuccess"}`) }}</p>
                            </button>

                            <button
                                v-if="resultPreview.failedImports.length"
                                class="modal__button modal__button--submit button button--default button--download download-error-btn"
                                @click="downloadErrorRowsDirectly"
                            >
                                <img src="../assets/icons/default/download_icon.svg" /> <br />
                                <p>{{ $t(`settingsPage.${"downloadError"}`) }}</p>
                            </button>
                        </div>

                        <button
                            v-if="$checkRolePermission('provision_import_submit')"
                            class="modal__button modal__button--submit button button--default"
                            @click="showSubmitter = true"
                        >
                            {{ $t(`buttons.${"saveBtn"}`) }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="modal__submit" v-if="showSubmitter && $checkRolePermission('provision_import_submit')">
                <div class="modal__submit-panel">
                    <div class="modal__submit-icon"></div>
                    <div class="modal__submit-border"></div>
                    <div class="modal__submit-container">
                        <div class="modal__submit-text">{{ $t(`settingsPage.${"downloadConfirm"}`) }}?</div>
                        <div class="modal__submit-buttons">
                            <button class="modal__submit-button" @click="sendDataToServer">
                                {{ $t(`buttons.${"yesBtn"}`) }}
                            </button>
                            <button class="modal__submit-button" @click="showSubmitter = false">
                                {{ $t(`buttons.${"cancelBtn"}`) }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script> 
import MultiSelectDropdown from "@/partials/MultiSelectDropdown";
import { mapGetters } from "vuex";
import DropFile from "./DropFile.vue";
import api from "@/api";
import * as XLSX from "@e965/xlsx";

export default {
    name: "PolicyProvisionImportModal",
    components: {
        DropFile,
        MultiSelectDropdown, 
    },
    data() {
        return {
            csvData: [],
            headers: [],
            lines: [],
            delimiter: ",",
            nameField: "",
            policyNumberField: "",
            provisionField: "",
            dateField: "",
            companyField: "",
            branchField: "",
            isRequesting: false,
            successfullyImported: [],
            failedImports: [],
            skippedRows: [],
            showInputFile: true,
            teamIds: [],
            teams: null,
            resultPreview: null,
            showCvsData: false,
            showSubmitter: false,
        };
    },
    computed: {
        ...mapGetters([
            "userForms",
        ]),
        canProcessCSV() {
            return (
                this.nameField &&
                this.policyNumberField &&
                this.provisionField &&
                this.dateField &&
                this.companyField &&
                this.branchField
            );
        },
    },
    methods: {
        handleFileUpload(files) {
            if (this.teamIds !== null && this.teamIds.length > 0) {
                const file = files[0].file;
                if (file) {
                    if (!this.isValidCSV(file)) {
                        this.$store.commit("setToast", {
                            display: true,
                            type: "error",
                            message: this.$i18n.t("messages.invalidFile"),
                        });
                        return;
                    }

                    const reader = new FileReader();
                    if (file.type === "text/csv" || file.type === "application/vnd.ms-excel") {
                        reader.onload = this.processCSV;
                        reader.readAsText(file);
                    } else if (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
                        reader.onload = (event) => {
                            const data = new Uint8Array(event.target.result);
                            const workbook = XLSX.read(data, {
                                type: "array",
                            });
                            const sheetName = workbook.SheetNames[0];
                            const sheet = workbook.Sheets[sheetName];
                            const csv = XLSX.utils.sheet_to_csv(sheet);
                            this.processCSV({
                                target: {
                                    result: csv,
                                },
                            });
                        };
                        reader.readAsArrayBuffer(file);
                    }

                    this.showInputFile = false;
                }
            }
        },
        isValidCSV(file) {
            const validTypes = [
                "text/csv",
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ];
            if (!validTypes.includes(file.type)) {
                return false;
            }

            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                return false;
            }

            return true;
        },
        detectDelimiter(csv) {
            const delimiters = [",", ";", "\t"];
            let bestDelimiter = delimiters[0];
            let maxColumns = 0;

            const normalizedCsv = csv.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
            const lines = normalizedCsv.split("\n").filter((line) => line.trim() !== "");

            if (lines.length === 0) return bestDelimiter;

            delimiters.forEach((delimiter) => {
                const sampleLines = lines.slice(0, Math.min(3, lines.length));
                let totalColumns = 0;
                let validLines = 0;

                sampleLines.forEach((line) => {
                    if (line.trim()) {
                        const columns = line.split(delimiter).length;
                        totalColumns += columns;
                        validLines++;
                    }
                });

                const avgColumns = validLines > 0 ? totalColumns / validLines : 0;
                if (avgColumns > maxColumns) {
                    maxColumns = avgColumns;
                    bestDelimiter = delimiter;
                }
            });

            return bestDelimiter;
        },
        parseCSVWithQuotes(csv, delimiter) {
            const lines = [];
            let currentRow = [];
            let currentField = "";
            let inQuotes = false;
            let i = 0;

            while (i < csv.length) {
                const char = csv[i];
                const nextChar = csv[i + 1];

                if (char === '"') {
                    if (inQuotes && nextChar === '"') {
                        currentField += '"';
                    } else {
                        inQuotes = !inQuotes;
                    }
                } else if (char === delimiter && !inQuotes) {
                    currentRow.push(currentField.trim());
                    currentField = "";
                } else if (char === "\n" && !inQuotes) {
                    currentRow.push(currentField.trim());
                    if (currentRow.some((field) => field !== "")) {
                        lines.push(currentRow);
                    }
                    currentRow = [];
                    currentField = "";
                } else {
                    if (char === "\n" && inQuotes) {
                        currentField += " ";
                    } else {
                        currentField += char;
                    }
                }
                i++;
            }

            if (currentField !== "" || currentRow.length > 0) {
                currentRow.push(currentField.trim());
                if (currentRow.some((field) => field !== "")) {
                    lines.push(currentRow);
                }
            }

            return lines;
        },
        processCSV(event) {
            const csv = event.target.result.trim();
            const normalizedCsv = csv.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
            const delimiter = this.detectDelimiter(normalizedCsv);
            this.delimiter = delimiter;

            const parsedLines = this.parseCSVWithQuotes(normalizedCsv, delimiter);
            this.lines = parsedLines;

            if (parsedLines.length > 0) {
                this.headers = parsedLines[0].map((header) => header.trim().replace(/^"|"$/g, ""));
            }
        },
        processMappedData() {
            if (this.lines.length > 1 && this.canProcessCSV) {
                const result = [];
                this.skippedRows = [];

                const nameIndex = this.headers.indexOf(this.nameField);
                const policyNumberIndex = this.headers.indexOf(this.policyNumberField);
                const provisionIndex = this.headers.indexOf(this.provisionField);
                const dateIndex = this.headers.indexOf(this.dateField);
                const companyIndex = this.headers.indexOf(this.companyField);
                const branchIndex = this.headers.indexOf(this.branchField);

                if (
                    nameIndex === -1 ||
                    policyNumberIndex === -1 ||
                    provisionIndex === -1 ||
                    dateIndex === -1 ||
                    companyIndex === -1 ||
                    branchIndex === -1
                ) {
                    this.$store.commit("setToast", {
                        display: true,
                        type: "error",
                        message: this.$i18n.t("messages.invalidFields"),
                    });
                    return;
                }

                for (let i = 1; i < this.lines.length; i++) {
                    const fields = this.lines[i];
                    const nonEmptyFields = fields.filter((field) => field !== null && field !== undefined && String(field).trim() !== "");
                    if (nonEmptyFields.length === 0) {
                        this.skippedRows.push({
                            rowNumber: i,
                            rawLine: fields.join(this.delimiter),
                            reason: "All fields are empty",
                            fields,
                        });
                        continue;
                    }

                    const requiredColumns = Math.max(nameIndex, policyNumberIndex, provisionIndex, dateIndex, companyIndex, branchIndex) + 1;
                    if (fields.length < requiredColumns) {
                        this.skippedRows.push({
                            rowNumber: i,
                            rawLine: fields.join(this.delimiter),
                            reason: `Insufficient columns (has ${fields.length}, needs ${requiredColumns})`,
                            fields,
                            expectedColumns: requiredColumns,
                        });
                        continue;
                    }

                    const obj = {
                        name: (fields[nameIndex] || "").trim(),
                        policyNumber: (fields[policyNumberIndex] || "").trim(),
                        provision: (() => {
                            const provisionStr = (fields[provisionIndex] || "").replace(/[^\d.-]/g, "");
                            const parsed = parseFloat(provisionStr);
                            return isNaN(parsed) ? 0 : parsed;
                        })(),
                        date: (fields[dateIndex] || "").trim(),
                        company: (fields[companyIndex] || "").trim(),
                        branch: (fields[branchIndex] || "").trim(),
                    };

                    if ((!obj.name || obj.name.trim() === "") && (!obj.policyNumber || obj.policyNumber.trim() === "")) {
                        this.skippedRows.push({
                            rowNumber: i,
                            rawLine: fields.join(this.delimiter),
                            reason: "Missing essential data (name and policy number)",
                            fields,
                            parsedData: obj,
                        });
                        continue;
                    }

                    result.push(obj);
                }

                this.csvData = result;
                this.showCvsData = true;

                if (this.skippedRows.length > 0) {
                    this.$store.commit("setToast", {
                        display: true,
                        type: "warning",
                        message: `Processed ${result.length} rows successfully. ${this.skippedRows.length} rows were skipped - see details below.`,
                    });
                } else {
                    this.$store.commit("setToast", {
                        display: true,
                        type: "success",
                        message: `Successfully processed all ${result.length} rows.`,
                    });
                }
            }
        },
        downloadSuccessRows() {
            const rows = this.successfullyImported.length
                ? this.successfullyImported
                : (this.resultPreview?.successfullyImported || []);

            if (!rows.length) return;

            const groupedByTeam = {};
            rows.forEach((item) => {
                const team = (item.team || "").trim();
                if (!groupedByTeam[team]) {
                    groupedByTeam[team] = [];
                }
                groupedByTeam[team].push(item);
            });

            let sheetData = [["Name", "Policy Number", "Team", "Agent", "Provision", "Date", "Company", "Branch"]];
            Object.entries(groupedByTeam).forEach(([team, items]) => {
                let total = 0;
                items.forEach((item) => {
                    total += Number(item.provision || 0);
                    sheetData.push([
                        item.fullname,
                        item.policyNumber,
                        team,
                        item.agent,
                        item.provision,
                        item.date,
                        item.company,
                        item.branch,
                    ]);
                });
                sheetData.push(["", "", "", "Total:", total, "", "", ""]);
                sheetData.push([]);
            });

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Success Rows");

            const now = new Date();
            const date = now.toLocaleDateString("en-GB").replace(/\//g, "-");
            const time = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }).replace(":", "-");
            const fileName = `successful_rows_(${date})(${time}).xlsx`;

            XLSX.writeFile(wb, fileName);
        },
        downloadErrorRows() {
            const rows = this.failedImports.length
                ? this.failedImports
                : (this.resultPreview?.failedImports || []);

            if (!rows.length) return;

            let sheetData = [["Name", "Policy Number", "Provision", "Error", "Date", "Company", "Branch"]];
            let total = 0;

            rows.forEach((item) => {
                total += Number(item.provision || 0);
                sheetData.push([
                    item.fullname,
                    item.policyNumber,
                    item.provision,
                    item.error,
                    item.date,
                    item.company,
                    item.branch,
                ]);
            });

            sheetData.push([]);
            sheetData.push(["", "Total:", total]);

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Error Rows");

            const now = new Date();
            const date = now.toLocaleDateString("en-GB").replace(/\//g, "-");
            const time = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }).replace(":", "-");
            const fileName = `error_rows_(${date})(${time}).xlsx`;
            XLSX.writeFile(wb, fileName);
        },
        async sendDataToServer() {
            try {
                this.showSubmitter = false;
                this.isRequesting = true;
                const response = await api({ requiresAuth: true }).post(`/v1/import-policy-provisions`, {
                    csvData: this.csvData,
                    team_id: this.teamIds,
                });

                const responseData = response.data;
                this.successfullyImported = responseData.successfullyImported || [];
                this.failedImports = responseData.failedImports || [];

                if (this.successfullyImported.length) {
                    this.downloadSuccessRows();
                }
                if (this.failedImports.length) {
                    this.downloadErrorRows();
                }

                this.csvData = [];
                this.headers = [];
                this.lines = [];
                this.nameField = "";
                this.policyNumberField = "";
                this.provisionField = "";
                this.dateField = "";
                this.companyField = "";
                this.branchField = "";
                this.showInputFile = true;

                this.$store.commit("setToast", {
                    display: true,
                    type: "success",
                    message: responseData.message,
                });

                this.showSubmitter = false;
            } catch (error) {
                const errorData = error.response?.data?.message || "An error occurred while processing the CSV.";
                this.$store.commit("setToast", {
                    display: true,
                    type: "error",
                    message: errorData,
                });
            } finally {
                this.isRequesting = false;
                await this.closeModal();
            }
        },
        downloadErrorRowsDirectly() {
            const rows = this.resultPreview?.failedImports || [];
            if (!rows.length) return;

            const sheetData = [["Name", "Policy Number", "Provision", "Error Message", "Date", "Company", "Branch"]];
            rows.forEach((item) => {
                sheetData.push([
                    item.fullname || "",
                    item.policyNumber || "",
                    item.provision || "",
                    item.error || "",
                    item.date || "",
                    item.company || "",
                    item.branch || "",
                ]);
            });

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Error Rows");

            const now = new Date();
            const date = now.toLocaleDateString("en-GB").replace(/\//g, "-");
            const time = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }).replace(":", "-");
            const fileName = `error_rows_(${date})(${time}).xlsx`;
            XLSX.writeFile(wb, fileName);
        },
        downloadSuccessRowsDirectly() {
            const rows = this.successfullyImported.length
                ? this.successfullyImported
                : (this.resultPreview?.successfullyImported || []);
            if (!rows.length) return;

            const groupedByTeam = {};
            rows.forEach((item) => {
                const team = (item.team || "").trim();
                if (!groupedByTeam[team]) {
                    groupedByTeam[team] = [];
                }
                groupedByTeam[team].push(item);
            });

            let sheetData = [["Name", "Policy Number", "Team", "Agent", "Provision", "Date", "Company", "Branch"]];
            Object.entries(groupedByTeam).forEach(([team, items]) => {
                let total = 0;
                items.forEach((item) => {
                    total += Number(item.provision || 0);
                    sheetData.push([
                        item.fullname,
                        item.policyNumber,
                        team,
                        item.agent,
                        item.provision,
                        item.date,
                        item.company,
                        item.branch,
                    ]);
                });
                sheetData.push(["", "", "", "Total:", total, "", "", ""]);
                sheetData.push([]);
            });

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Success Rows");

            const now = new Date();
            const date = now.toLocaleDateString("en-GB").replace(/\//g, "-");
            const time = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }).replace(":", "-");
            const fileName = `successful_rows_(${date})(${time}).xlsx`;
            XLSX.writeFile(wb, fileName);
        },
        async sendDataToPreview() {
            try {
                this.showCvsData = false;
                this.isRequesting = true;
                const response = await api({ requiresAuth: true }).post(`/v1/import-policy-provisions-preview`, {
                    csvData: this.csvData,
                    team_id: this.teamIds,
                });

                this.resultPreview = response.data;

                const fileInput = this.$refs.fileInput;
                if (fileInput) {
                    fileInput.value = "";
                }

                this.showInputFile = false;

                this.$store.commit("setToast", {
                    display: true,
                    type: "success",
                    message: response.data.message,
                });
            } catch (error) {
                const errorData = error.response?.data?.message || "An error occurred while processing the CSV.";
                this.$store.commit("setToast", {
                    display: true,
                    type: "error",
                    message: errorData,
                });
            } finally {
                this.isRequesting = false;
            }
        },
        async closeModal() {
            this.resultPreview = null;
            this.csvData = [];
            this.headers = [];
            this.lines = [];
            this.delimiter = ",";
            this.skippedRows = [];
            this.nameField = "";
            this.policyNumberField = "";
            this.provisionField = "";
            this.dateField = "";
            this.companyField = "";
            this.branchField = "";
            this.showInputFile = true;
            this.showCvsData = false;
            this.showSubmitter = false;

            this.$store.commit("setShowPolicyProvisionImportModal", false);
        },
    },
    async mounted() {
        if (!this.$checkRolePermission("provision_import_preview")) {
            this.closeModal();
        } else {
            this.isRequesting = true;
            await this.$store.dispatch("getUserForms");
            this.teams = this.userForms.teams;
            this.isRequesting = false;
        }
    },
};
</script>

<style lang="scss" scoped>
@use "../assets/scss/add-panel-modal";

.wrapper {
    margin-bottom: 20px;
}

.download-buttons {
    display: flex;
    width: 90%;
    margin: 0 auto;
    margin-top: 10px;
    justify-content: center;
    gap: 20px;

    button {
        margin: 0 !important;
    }
}

.modal__submit {
    z-index: 99;
}

.modal {
    .import__form {
        width: 100% !important;
    }

    .modal__button--submit {
        right: 30px;
    }

    .form__row {
        justify-content: center;
    }

    &__panel {
        height: auto !important;
        width: 75%;
        min-height: 65% !important;
    }

    &__provision {
        padding: 28px 0;

        &-title {
            font-size: var(--medium);
        }

        &-title,
        &-label {
            color: var(--text-2);
        }

        &-label {
            margin-right: 10px;
        }

        &-data {
            color: var(--text-9);
            font-family: var(--muli-text-semi-bold);
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            max-width: 335px;
            display: flex;
        }

        &-container {
            margin-top: 25px;
            padding-top: 15px;
            border-top: 1px solid var(--border-10);
        }

        &-statuses {
            max-height: 800px;
            overflow: visible;
            overflow-y: scroll;
        }

        &-row {
            display: flex;
            flex-wrap: wrap;
            padding: 0 50px;

            .form__field {
                width: calc(50% - 10px);
                background: var(--bg-23);
                padding: 15px;
                border-radius: var(--radius-1);
                display: flex;
                align-items: center;
                margin-bottom: 15px;

                &--margin {
                    margin-right: 20px;
                }

                &--status {
                    cursor: pointer;
                }

                &--paid {
                    background: var(--bg-25);
                    border: 1px solid var(--border-2);

                    .modal__provision-data {
                        color: var(--text-5);
                    }
                }

                &--unpaid {
                    background: var(--bg-24);

                    .modal__provision-data {
                        color: var(--text-12);
                    }
                }

                &--supervisors {
                    width: 100%;
                }

                &--empty {
                    background: 0;
                }
            }

            .button {
                background-color: var(--bg-2);
                justify-content: center;
                font-family: var(--muli-text-regular);

                &:hover {
                    background-color: var(--hover-1);
                }
            }
        }

        &-arrow {
            background: url("../assets/icons/default/next_arrow.svg");
            height: 16px;
            width: 16px;
            margin: 0 10px;
        }
    }

    .add__new,
    .add__title {
        color: var(--text-5);
        text-align: center;

        margin-bottom: 30px;
    }

    &__submit {
        position: absolute;
        height: 100%;
        width: 100%;
        background: var(--modal-bg-2);
        backdrop-filter: blur(10px);
        top: 0;
        left: 0;
        border-radius: inherit;
        display: flex;
        align-items: center;
        justify-content: center;

        &-panel {
            width: 370px;
            padding: 9px 15px;
            background: var(--text-5);
            border-radius: var(--radius-1);
            display: flex;
            align-items: center;
        }

        &-icon {
            background: url("../assets/icons/default/question_icon.svg");
            min-width: 30px;
            height: 30px;
        }

        &-border {
            width: 1px;
            height: 70px;
            margin: 0 15px;
            background: var(--border-16);
        }

        &-container {
            flex-direction: column;
            align-items: flex-start;
        }

        &-buttons,
        &-container {
            display: flex;
            justify-content: center;
        }

        &-button {
            border-radius: var(--radius-1);
            box-shadow: 0px 3px 6px var(--shadow-3);
            color: var(--text-2);
            padding: 5px 0 5px 0;
            width: 100px;
            transition: all 0.3s ease;

            &:hover {
                color: var(--text-5);
                box-shadow: none;
            }

            &:first-child {
                margin-right: 50px;
            }

        }

        &-text {
            color: var(--text-1);
            margin-bottom: 15px;
        }
    }

    .sample__csv {
        margin-top: 30px;

        :hover {
            text-decoration: underline;
        }

    }

    .button--download {
        background: white;
        color: #7F8FA4;
        box-shadow: none;
        font-weight: normal;
        font-size: 14px;
        width: 300px;
        height: 50px;

        &:hover {
            color: var(--text-5);

        }

        img {
            width: 30px;
        }
    }
}

.team-row {
    width: 30%;
    margin: 20px auto;

    .multi {
        width: 100%;
    }
}

.results {

    h4,
    h5 {
        color: #22DB8E;
        font-size: 14px;
        font-weight: normal;
        margin-bottom: 10px;
        text-align: center;
    }

    h5 {
        color: #7F8FA4;
        margin-top: 20px;
    }

}

.processed__table {
    width: 60% !important;
    margin: 0 auto !important;
}

.skipped__table {
    width: 80% !important;
    margin: 20px auto !important;

    .form__title {
        color: #FFA500 !important;
        /* Orange for skipped rows */
    }

    table {
        border: 2px solid #FFA500;
    }

    th {
        background-color: #FFF3CD !important;
        color: #856404 !important;
    }

    .raw-data {
        font-family: monospace;
        font-size: 12px;
        max-width: 200px;
        word-break: break-all;
    }
}

.results {
    width: 100% !important;
    margin: 0 auto !important;
}

.processed__table,
.results {
    max-height: 300px;
    overflow: scroll;
}

.map__fields {
    width: 58% !important;
    margin: 10px auto !important;

    .form__title {
        color: #7F8FA4;
        font-weight: normal;
    }
}

.form__title {
    margin-top: 10px;
    margin-bottom: 20px;

}

table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    /* No spacing between rows */
    margin-top: 20px;
    border-spacing: 6px 0px;
}

th,
td {
    padding: 16px;
    text-align: left;
    background-color: #fff;
    box-shadow: 0px 0px 10px #0000000D;
    border-bottom: 1px solid #DADCDE;
    border-radius: 0px;

}

td {
    font: normal normal 600 14px/18px;
    letter-spacing: 0px;
    color: #7F8FA4;
}

tr:first-child th {

    border-radius: 8px 8px 0px 0px;
}

tr:last-child td {
    border-radius: 0px 0px 8px 8px;
    border: none;
}

th {
    color: #22DB8E;
    font-weight: normal;
    font-size: 14px;
}

tbody tr+tr td {
    margin-top: 15px;
    /* Adds a gap between rows without separating cells */
}

tbody tr:hover td {
    background-color: #f1f1f1;
}

.download-error-btn {
    position: relative !important;
    right: 0 !important;
    bottom: 0 !important;
    margin: 20px auto !important;
    margin-top: 50px !important;
    padding: 5px 0 !important;
    height: auto !important;
}


.requesting-el {
    min-height: 4em;
    width: 4em;
    margin: 0 auto;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--bg-2);
    background-image: url("../assets/icons/default/three-dots.svg");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 100%;
    border-radius: 50%;
}

[data-theme="dark"] tr th,
[data-theme="dark"] tr td,
[data-theme="dark"] .download-error-btn {
    background: var(--bg-17) !important;
}

[data-theme="dark"] tr td,
[data-theme="dark"] .processed__table .form__title,
[data-theme="dark"] .sample__csv a,
[data-theme="dark"] .download-error-btn,
[data-theme="dark"] .results h5 {
    color: white !important;
}

.error-message {
    white-space: pre-line;
    word-break: break-word;
}
</style>
